// Channel list for the in-progress strategy. Defaults the first channel to the
// TestUi's own /webhooks/sink so a fresh strategy delivers locally for inspection.
//
// Test-only scope: delivery is the rich JSON webhook (observed in the TestUi Alert
// feed). Integration with the live trade-execution backend is intentionally NOT here.

import { useEffect } from "react";
import { useBuilderState } from "../../state/BuilderState";
import { URLS } from "../../api/client";
import { button, COLORS, dangerButton, fieldLabel, input, panel } from "../shell/theme";
import { SectionHeader } from "../shell/SectionHeader";

// The URL the *orchestrator* hits when delivering — NOT the URL the browser uses to view.
// In prod the browser hits the TestUi over public HTTPS via nginx (URLS.testUi === ""), but
// the orchestrator container hits the TestUi over the shared Docker network using compose's
// service-name DNS. So in prod we hardcode the Docker DNS form; in local dev (where
// URLS.testUi resolves to http://localhost:5180), we use that so the dev-orchestrator can
// still reach the sink on the host loopback.
const DEFAULT_WEBHOOK_URL = URLS.testUi
  ? `${URLS.testUi}/webhooks/sink`
  : "http://TestUi:8080/webhooks/sink";

export function ChannelSection() {
  const { draft, patch } = useBuilderState();

  // Seed the first channel pointing at our own webhook sink so the smoke path works
  // out of the box. Only seed if the channels list is empty (don't overwrite user choices).
  useEffect(() => {
    if (draft.channels.length === 0) {
      patch({
        channels: [{ type: "webhook", url: DEFAULT_WEBHOOK_URL, enabled: true }],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUrl = (i: number, url: string) => {
    const next = [...draft.channels];
    next[i] = { ...next[i]!, url };
    patch({ channels: next });
  };

  const remove = (i: number) => {
    patch({ channels: draft.channels.filter((_, idx) => idx !== i) });
  };

  const add = () => {
    patch({
      channels: [...draft.channels, { type: "webhook", url: "", enabled: true }],
    });
  };

  const seedTestUi = () => {
    patch({
      channels: [...draft.channels, { type: "webhook", url: DEFAULT_WEBHOOK_URL, enabled: true }],
    });
  };

  return (
    <section style={panel}>
      <SectionHeader
        title="Webhook channels"
        subtitle="Where alerts get delivered when the strategy fires. The orchestrator HTTP-POSTs the rich JSON payload to each URL, signed with an HMAC header so receivers can verify authenticity."
        help={`Each fired alert produces an HTTP POST with: Content-Type: application/json, X-Webhook-Signature: sha256=<hex>, X-Webhook-Fingerprint: <id>. The body has strategyId / signalType / direction / coin / signalBarTime / expectedEntryPrice / suggestedSL / suggestedTP / score / winRate. Retries 3× with exponential backoff on non-2xx. The default URL points at this UI's own /webhooks/sink so you can see what bot endpoints will receive.`}
        badge={`${draft.channels.length} channel${draft.channels.length === 1 ? "" : "s"}`}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
        {draft.channels.map((c, i) => (
          <div key={i} style={{ display: "flex", alignItems: "end", gap: 6 }}>
            <label style={{ flex: 1 }}>
              <div style={fieldLabel}>Webhook URL #{i + 1}</div>
              <input
                value={c.url}
                onChange={(e) => updateUrl(i, e.target.value)}
                placeholder="https://your-bot.example/webhook"
                style={{ ...input, fontFamily: "monospace", fontSize: 12 }}
              />
            </label>
            <button onClick={() => remove(i)} style={{ ...dangerButton, padding: "4px 10px", fontSize: 11 }} title="Remove this channel">
              ×
            </button>
          </div>
        ))}
        {draft.channels.length === 0 && (
          <div style={{ padding: "8px 10px", background: COLORS.bgRaised, borderRadius: 4, border: `1px dashed ${COLORS.border}` }}>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>
              No channels — saving will create the strategy with zero delivery targets.
              It still persists alerts to the DB, but can't notify anything externally.
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button onClick={seedTestUi} style={{ ...button, fontSize: 11 }}>
                + Add TestUi sink (recommended for testing)
              </button>
              <button onClick={add} style={{ ...button, fontSize: 11 }}>
                + Add custom URL
              </button>
            </div>
          </div>
        )}
        {draft.channels.length > 0 && (
          <button onClick={add} style={{ ...button, alignSelf: "flex-start", fontSize: 11 }}>
            + Add another channel
          </button>
        )}
      </div>
    </section>
  );
}
